"use client";

import { c as _c } from "react/compiler-runtime";
import { Activity, Blocks, BookOpen, Bot, Boxes, Building2, Calculator, ChevronDown, Clock, Code, GitCompare, Gift, Github, Handshake, KeyRound, LayoutGrid, Menu, MessagesSquare, Network, Newspaper, ScrollText, Server, Terminal, Shield, ShieldCheck, Sparkles, Trophy, Wrench, X, Zap } from "lucide-react";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import { AuthLink } from "@/components/shared/auth-link";
import { ModelSearch } from "@/components/shared/model-search";
import { useSessionStatus } from "@/hooks/useUser";
import { Button } from "@/lib/components/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/lib/components/navigation-menu";
import { useAppConfig } from "@/lib/config";
import { LogoLockup } from "@/lib/icons/Logo";
import { useSystemBanner } from "@/lib/system-banner-context";
import { cn } from "@/lib/utils";
import { MARKETING_STATS } from "@llmgateway/shared";
import { SystemBannerBar } from "@llmgateway/shared/system-banner";
import { ProviderPromoBanner } from "./provider-promo-banner";
import { ThemeToggle } from "./theme-toggle";
import type { Route } from "next";
function IconMenuItem(t0) {
  const $ = _c(32);
  const {
    title,
    href,
    description,
    icon: IconComponent,
    gradient,
    external
  } = t0;
  const posthog = usePostHog();
  let t1;
  if ($[0] !== gradient) {
    t1 = cn("group/product flex flex-row items-start gap-3 select-none rounded-lg p-3 no-underline outline-none transition-all duration-300 bg-linear-to-br from-transparent to-transparent", gradient, "hover:shadow-lg focus:shadow-md");
    $[0] = gradient;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const linkClassName = t1;
  let T0;
  let t2;
  let t3;
  if ($[2] !== IconComponent || $[3] !== gradient) {
    const iconColor = gradient.split(" ").slice(-2).join(" ");
    t3 = "flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted/80 transition-colors";
    T0 = IconComponent;
    t2 = cn("h-4 w-4 text-muted-foreground transition-colors", iconColor);
    $[2] = IconComponent;
    $[3] = gradient;
    $[4] = T0;
    $[5] = t2;
    $[6] = t3;
  } else {
    T0 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  let t4;
  if ($[7] !== T0 || $[8] !== t2) {
    t4 = <T0 className={t2} />;
    $[7] = T0;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== title) {
    t6 = <div className="text-sm font-medium leading-none">{title}</div>;
    $[13] = title;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== description) {
    t7 = <p className="line-clamp-2 text-xs leading-snug text-muted-foreground">{description}</p>;
    $[15] = description;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <div className="space-y-0.5">{t6}{t7}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t5 || $[21] !== t8) {
    t9 = <>{t5}{t8}</>;
    $[20] = t5;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const inner = t9;
  let t10;
  if ($[23] !== posthog || $[24] !== title) {
    t10 = () => {
      posthog.capture("nav_link_clicked", {
        link: title,
        area: "dropdown"
      });
    };
    $[23] = posthog;
    $[24] = title;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const handleClick = t10;
  let t11;
  if ($[26] !== external || $[27] !== handleClick || $[28] !== href || $[29] !== inner || $[30] !== linkClassName) {
    t11 = <li><NavigationMenuLink asChild={true}>{external ? <a href={href} target="_blank" rel="noopener noreferrer" className={linkClassName} onClick={handleClick}>{inner}</a> : <Link href={href as Route} prefetch={true} className={linkClassName} onClick={handleClick}>{inner}</Link>}</NavigationMenuLink></li>;
    $[26] = external;
    $[27] = handleClick;
    $[28] = href;
    $[29] = inner;
    $[30] = linkClassName;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
}
export const Navbar = t0 => {
  const $ = _c(87);
  const {
    children,
    sticky: t1
  } = t0;
  const sticky = t1 === undefined ? true : t1;
  const config = useAppConfig();
  const posthog = usePostHog();
  const {
    isAuthenticated: hasSession,
    isLoading
  } = useSessionStatus();
  const isAuthenticated = hasSession && !isLoading;
  let t2;
  if ($[0] !== posthog) {
    t2 = link => {
      posthog.capture("nav_link_clicked", {
        link,
        area: "navbar"
      });
    };
    $[0] = posthog;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const trackNav = t2;
  const productsLinks = [{
    title: "AI Gateway",
    href: "/products/ai-gateway",
    description: `Route requests to ${MARKETING_STATS.models} models through one API.`,
    icon: Network,
    gradient: "hover:from-violet-500/20 hover:to-purple-600/30 hover:shadow-violet-500/10 group-hover/product:text-violet-500 dark:group-hover/product:text-violet-400"
  }, {
    title: "DevPass",
    href: "/products/devpass",
    description: "Fixed-price monthly plans for Claude Code, Cursor chat and other tools that accept a custom OpenAI or Anthropic URL.",
    icon: Code,
    gradient: "hover:from-indigo-500/20 hover:to-blue-600/30 hover:shadow-indigo-500/10 group-hover/product:text-indigo-500 dark:group-hover/product:text-indigo-400"
  }, {
    title: "Lounge",
    href: "/products/lounge",
    description: "Frontier models from OpenAI, Anthropic, Google and xAI in one chat (full lineup on Plus and Pro), plus image, video and audio studios.",
    icon: MessagesSquare,
    gradient: "hover:from-blue-500/20 hover:to-cyan-600/30 hover:shadow-blue-500/10 group-hover/product:text-blue-500 dark:group-hover/product:text-blue-400"
  }, {
    title: "Observability",
    href: "/products/observability",
    description: "Monitor usage, costs, and latency with real-time analytics dashboards.",
    icon: Activity,
    gradient: "hover:from-emerald-500/20 hover:to-teal-600/30 hover:shadow-emerald-500/10 group-hover/product:text-emerald-500 dark:group-hover/product:text-emerald-400"
  }];
  const resourcesLinks = [{
    title: "Enterprise",
    href: "/enterprise",
    description: "Custom billing, SSO, guardrails and priority support for teams.",
    icon: Building2,
    gradient: "hover:from-blue-500/20 hover:to-blue-600/30 hover:shadow-blue-500/10 group-hover/product:text-blue-500 dark:group-hover/product:text-blue-400"
  }, {
    title: "Blog",
    href: "/blog",
    description: "Product updates, tutorials, benchmarks, and announcements.",
    icon: Newspaper,
    gradient: "hover:from-amber-500/20 hover:to-orange-600/30 hover:shadow-amber-500/10 group-hover/product:text-amber-500 dark:group-hover/product:text-amber-400"
  }, {
    title: "Changelog",
    href: "/changelog",
    description: "What's new in LLM Gateway across releases.",
    icon: ScrollText,
    gradient: "hover:from-violet-500/20 hover:to-purple-600/30 hover:shadow-violet-500/10 group-hover/product:text-violet-500 dark:group-hover/product:text-violet-400"
  }, {
    title: "Integrations",
    href: "/guides",
    description: "Connect seamlessly with popular frameworks, SDKs, and tools.",
    icon: Blocks,
    gradient: "hover:from-indigo-500/20 hover:to-blue-600/30 hover:shadow-indigo-500/10 group-hover/product:text-indigo-500 dark:group-hover/product:text-indigo-400"
  }, {
    title: "Reliability",
    href: "/reliability",
    description: "Automatic failover to another provider, for models with more than one, when one errors or times out.",
    icon: ShieldCheck,
    gradient: "hover:from-emerald-500/20 hover:to-teal-600/30 hover:shadow-emerald-500/10 group-hover/product:text-emerald-500 dark:group-hover/product:text-emerald-400"
  }, {
    title: "Features",
    href: "/features",
    description: "Routing, multimodal APIs, cost tracking, compliance and more, at a glance.",
    icon: Sparkles,
    gradient: "hover:from-sky-500/20 hover:to-indigo-600/30 hover:shadow-sky-500/10 group-hover/product:text-sky-500 dark:group-hover/product:text-sky-400"
  }, {
    title: "Guardrails",
    href: "/features/guardrails",
    description: "Enterprise: flag PII, secrets and common prompt-injection phrases before a provider sees them.",
    icon: Shield,
    gradient: "hover:from-rose-500/20 hover:to-red-600/30 hover:shadow-rose-500/10 group-hover/product:text-rose-500 dark:group-hover/product:text-rose-400"
  }, {
    title: "Providers",
    href: "/providers",
    description: "Connect and manage your provider API keys.",
    icon: KeyRound,
    gradient: "hover:from-cyan-500/20 hover:to-blue-600/30 hover:shadow-cyan-500/10 group-hover/product:text-cyan-500 dark:group-hover/product:text-cyan-400"
  }, {
    title: "Partners",
    href: "/partners",
    description: "Our inference partner program, starting with SCX.ai.",
    icon: Handshake,
    gradient: "hover:from-teal-500/20 hover:to-emerald-600/30 hover:shadow-teal-500/10 group-hover/product:text-teal-500 dark:group-hover/product:text-teal-400"
  }, {
    title: "Rankings",
    href: "/rankings",
    description: "Top models by real token volume routed through the gateway.",
    icon: Trophy,
    gradient: "hover:from-amber-500/20 hover:to-yellow-600/30 hover:shadow-amber-500/10 group-hover/product:text-amber-500 dark:group-hover/product:text-amber-400"
  }, {
    title: "Apps",
    href: "/apps",
    description: "Browse apps and tools that work with LLM Gateway.",
    icon: LayoutGrid,
    gradient: "hover:from-pink-500/20 hover:to-rose-600/30 hover:shadow-pink-500/10 group-hover/product:text-pink-500 dark:group-hover/product:text-pink-400"
  }, {
    title: "Models",
    href: "/models",
    description: "Browse all available LLM models and capabilities.",
    icon: Boxes,
    gradient: "hover:from-purple-500/20 hover:to-fuchsia-600/30 hover:shadow-purple-500/10 group-hover/product:text-purple-500 dark:group-hover/product:text-purple-400"
  }, {
    title: "Model Timeline",
    href: "/timeline",
    description: "Track the release history of all models.",
    icon: Clock,
    gradient: "hover:from-teal-500/20 hover:to-cyan-600/30 hover:shadow-teal-500/10 group-hover/product:text-teal-500 dark:group-hover/product:text-teal-400"
  }, {
    title: "Compare",
    href: "/models/compare",
    description: "Compare models side by side.",
    icon: GitCompare,
    gradient: "hover:from-sky-500/20 hover:to-blue-600/30 hover:shadow-sky-500/10 group-hover/product:text-sky-500 dark:group-hover/product:text-sky-400"
  }, {
    title: "Token Cost Calculator",
    href: "/token-cost-calculator",
    description: "Estimate LLM token costs across providers.",
    icon: Calculator,
    gradient: "hover:from-green-500/20 hover:to-emerald-600/30 hover:shadow-green-500/10 group-hover/product:text-green-500 dark:group-hover/product:text-green-400"
  }, {
    title: "Referral Program",
    href: "/referrals",
    description: "Earn 1% of what your referrals spend, as credits.",
    icon: Gift,
    gradient: "hover:from-yellow-500/20 hover:to-amber-600/30 hover:shadow-yellow-500/10 group-hover/product:text-yellow-500 dark:group-hover/product:text-yellow-400"
  }];
  const aiLinks = [{
    title: "MCP Server",
    href: "/mcp",
    description: "Connect AI assistants to LLM Gateway models over MCP.",
    icon: Server,
    gradient: "hover:from-cyan-500/20 hover:to-blue-600/30 hover:shadow-cyan-500/10 group-hover/product:text-cyan-500 dark:group-hover/product:text-cyan-400"
  }, {
    title: "CLI",
    href: "https://docs.llmgateway.io/developers/cli",
    description: "Launch Claude Code, Codex and more pre-wired to LLM Gateway.",
    icon: Terminal,
    gradient: "hover:from-slate-500/20 hover:to-zinc-600/30 hover:shadow-slate-500/10 group-hover/product:text-slate-500 dark:group-hover/product:text-slate-300",
    external: true
  }, {
    title: "Agents",
    href: "/agents",
    description: "Open-source example agents, some with tool calling.",
    icon: Bot,
    gradient: "hover:from-violet-500/20 hover:to-purple-600/30 hover:shadow-violet-500/10 group-hover/product:text-violet-500 dark:group-hover/product:text-violet-400"
  }, {
    title: "AI SDK Provider",
    href: "https://github.com/theopenco/llmgateway-ai-sdk-provider",
    description: "Use LLM Gateway with Vercel's AI SDK.",
    icon: Zap,
    gradient: "hover:from-amber-500/20 hover:to-orange-600/30 hover:shadow-amber-500/10 group-hover/product:text-amber-500 dark:group-hover/product:text-amber-400",
    external: true
  }, {
    title: "Agent Skills",
    href: "https://github.com/theopenco/agent-skills",
    description: "An image generation skill for Claude Code and other AI agents.",
    icon: Sparkles,
    gradient: "hover:from-pink-500/20 hover:to-rose-600/30 hover:shadow-pink-500/10 group-hover/product:text-pink-500 dark:group-hover/product:text-pink-400",
    external: true
  }, {
    title: "Templates",
    href: "/templates",
    description: "Production-ready templates for AI applications.",
    icon: Wrench,
    gradient: "hover:from-emerald-500/20 hover:to-teal-600/30 hover:shadow-emerald-500/10 group-hover/product:text-emerald-500 dark:group-hover/product:text-emerald-400"
  }, {
    title: "Guides",
    href: "/guides",
    description: "Setup guides for Claude Code, Cursor, Cline and more.",
    icon: BookOpen,
    gradient: "hover:from-blue-500/20 hover:to-indigo-600/30 hover:shadow-blue-500/10 group-hover/product:text-blue-500 dark:group-hover/product:text-blue-400"
  }];
  const mobileSections = [{
    label: "Products",
    items: productsLinks.map(_temp)
  }, {
    label: "Resources",
    items: resourcesLinks.map(_temp2)
  }, {
    label: "AI",
    items: aiLinks.map(_temp3)
  }];
  const systemBanner = useSystemBanner();
  const [menuState, setMenuState] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openMobileSection, setOpenMobileSection] = useState(null);
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const handleScroll = () => {
        setIsScrolled(window.scrollY > 50);
      };
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => window.removeEventListener("scroll", handleScroll);
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] !== systemBanner) {
    t5 = <SystemBannerBar banner={systemBanner} />;
    $[4] = systemBanner;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = sticky && "fixed";
  let t7;
  if ($[6] !== t6) {
    t7 = cn("z-20 w-full px-2 group", t6);
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== isScrolled) {
    t8 = <ProviderPromoBanner collapsed={isScrolled} />;
    $[8] = isScrolled;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = isScrolled && "bg-background/50 max-w-[1400px] rounded-2xl border backdrop-blur-lg lg:px-5";
  let t10;
  if ($[10] !== t9) {
    t10 = cn("mt-2 mx-auto max-w-[1400px] px-6 transition-all duration-300", t9);
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Link href="/" className="flex items-center" prefetch={true}><LogoLockup className="h-6 w-auto shrink-0 text-black dark:text-white" /></Link>;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== menuState) {
    t12 = () => setMenuState(!menuState);
    $[13] = menuState;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const t13 = menuState ? "Close Menu" : "Open Menu";
  let t14;
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Menu className="group-data-[state=active]:scale-0 group-data-[state=active]:opacity-0 size-6 duration-200" />;
    t15 = <X className="absolute inset-0 m-auto size-6 -rotate-180 scale-0 opacity-0 group-data-[state=active]:rotate-0 group-data-[state=active]:scale-100 group-data-[state=active]:opacity-100 duration-200" />;
    $[15] = t14;
    $[16] = t15;
  } else {
    t14 = $[15];
    t15 = $[16];
  }
  let t16;
  if ($[17] !== t12 || $[18] !== t13) {
    t16 = <div className="flex w-full justify-between nav:mr-4 nav:w-auto nav:shrink-0">{t11}<button onClick={t12} aria-label={t13} className="relative z-20 -m-2.5 -mr-4 block cursor-pointer p-2.5 nav:hidden">{t14}{t15}</button></div>;
    $[17] = t12;
    $[18] = t13;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="w-[140px] xl:w-[160px]"><ModelSearch /></div>;
    $[20] = t17;
  } else {
    t17 = $[20];
  }
  let t18;
  if ($[21] !== trackNav) {
    t18 = <NavigationMenuItem><NavigationMenuLink asChild={true}><a href="https://devpass.llmgateway.io" onClick={() => trackNav("DevPass")} className="text-muted-foreground hover:text-accent-foreground block duration-150 px-3 py-2 whitespace-nowrap">DevPass</a></NavigationMenuLink></NavigationMenuItem>;
    $[21] = trackNav;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  const t19 = config.playgroundUrl ?? "#";
  let t20;
  if ($[23] !== trackNav) {
    t20 = () => trackNav("Chat");
    $[23] = trackNav;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] !== t19 || $[26] !== t20) {
    t21 = <NavigationMenuItem className="hidden min-[1360px]:block"><NavigationMenuLink asChild={true}><a href={t19} onClick={t20} className="text-muted-foreground hover:text-accent-foreground block duration-150 px-3 py-2 whitespace-nowrap">Lounge</a></NavigationMenuLink></NavigationMenuItem>;
    $[25] = t19;
    $[26] = t20;
    $[27] = t21;
  } else {
    t21 = $[27];
  }
  let t22;
  if ($[28] !== trackNav) {
    t22 = <NavigationMenuItem><NavigationMenuLink asChild={true}><Link href="/models" prefetch={true} onClick={() => trackNav("Models")} className="text-muted-foreground hover:text-accent-foreground block duration-150 px-3 py-2 whitespace-nowrap">Models</Link></NavigationMenuLink></NavigationMenuItem>;
    $[28] = trackNav;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  let t23;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <NavigationMenuTrigger className="text-muted-foreground hover:text-accent-foreground px-3 py-2 bg-transparent">Products</NavigationMenuTrigger>;
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  let t24;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <NavigationMenuTrigger className="text-muted-foreground hover:text-accent-foreground px-3 py-2 bg-transparent">Resources</NavigationMenuTrigger>;
    $[31] = t24;
  } else {
    t24 = $[31];
  }
  let t25;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <NavigationMenuTrigger className="text-muted-foreground hover:text-accent-foreground px-3 py-2 bg-transparent">AI</NavigationMenuTrigger>;
    $[32] = t25;
  } else {
    t25 = $[32];
  }
  const t26 = config.docsUrl ?? "";
  let t27;
  if ($[33] !== trackNav) {
    t27 = () => trackNav("Docs");
    $[33] = trackNav;
    $[34] = t27;
  } else {
    t27 = $[34];
  }
  let t28;
  if ($[35] !== t26 || $[36] !== t27) {
    t28 = <NavigationMenuItem><NavigationMenuLink asChild={true}><a href={t26} target="_blank" rel="noopener noreferrer" onClick={t27} className="text-muted-foreground hover:text-accent-foreground block duration-150 px-3 py-2">Docs</a></NavigationMenuLink></NavigationMenuItem>;
    $[35] = t26;
    $[36] = t27;
    $[37] = t28;
  } else {
    t28 = $[37];
  }
  let t29;
  if ($[38] !== trackNav) {
    t29 = <NavigationMenuItem><NavigationMenuLink asChild={true}><Link href="/pricing" prefetch={true} onClick={() => trackNav("Pricing")} className="text-muted-foreground hover:text-accent-foreground block duration-150 px-3 py-2">Pricing</Link></NavigationMenuLink></NavigationMenuItem>;
    $[38] = trackNav;
    $[39] = t29;
  } else {
    t29 = $[39];
  }
  const t30 = "bg-background group-data-[state=active]:block nav:group-data-[state=active]:flex mb-6 hidden max-h-[calc(100dvh-7rem)] w-full flex-wrap items-center justify-end space-y-6 overflow-y-auto overscroll-contain rounded-3xl border p-6 shadow-2xl shadow-zinc-300/20 md:flex-nowrap nav:m-0 nav:flex nav:max-h-none nav:w-fit nav:shrink-0 nav:gap-3 nav:space-y-0 nav:overflow-visible nav:border-transparent nav:bg-transparent nav:p-0 nav:shadow-none dark:shadow-none dark:nav:bg-transparent";
  const t31 = "nav:hidden";
  let t32;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <div className="mb-4"><ModelSearch /></div>;
    $[40] = t32;
  } else {
    t32 = $[40];
  }
  const t33 = "text-base";
  let t34;
  if ($[41] !== trackNav) {
    t34 = <li><a href="https://devpass.llmgateway.io" onClick={() => trackNav("DevPass")} className="text-muted-foreground hover:text-accent-foreground block py-2.5 duration-150">DevPass</a></li>;
    $[41] = trackNav;
    $[42] = t34;
  } else {
    t34 = $[42];
  }
  const t35 = config.playgroundUrl ?? "#";
  let t36;
  if ($[43] !== trackNav) {
    t36 = () => trackNav("Chat");
    $[43] = trackNav;
    $[44] = t36;
  } else {
    t36 = $[44];
  }
  let t37;
  if ($[45] !== t35 || $[46] !== t36) {
    t37 = <li><a href={t35} onClick={t36} className="text-muted-foreground hover:text-accent-foreground block py-2.5 duration-150">Lounge</a></li>;
    $[45] = t35;
    $[46] = t36;
    $[47] = t37;
  } else {
    t37 = $[47];
  }
  let t38;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <li><Link href="/pricing" className="text-muted-foreground hover:text-accent-foreground block py-2.5 duration-150" prefetch={true}>Pricing</Link></li>;
    $[48] = t38;
  } else {
    t38 = $[48];
  }
  const t39 = config.docsUrl ?? "";
  let t40;
  if ($[49] !== t39) {
    t40 = <li><a href={t39} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent-foreground block py-2.5 duration-150">Docs</a></li>;
    $[49] = t39;
    $[50] = t40;
  } else {
    t40 = $[50];
  }
  let t41;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <li><Link href="/models" className="text-muted-foreground hover:text-accent-foreground block py-2.5 duration-150" prefetch={true}>Models</Link></li>;
    $[51] = t41;
  } else {
    t41 = $[51];
  }
  const t42 = mobileSections.map(section => <li key={section.label}><button type="button" onClick={() => setOpenMobileSection(openMobileSection === section.label ? null : section.label)} className="flex w-full items-center justify-between gap-2 py-2.5 text-left" aria-expanded={openMobileSection === section.label}><span className="text-muted-foreground">{section.label}</span><ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", openMobileSection === section.label && "rotate-180")} /></button><ul className={cn("grid grid-cols-2 gap-x-4 rounded-xl bg-muted/40 px-3 py-2 mb-2", openMobileSection !== section.label && "hidden")}>{section.items.map(_temp7)}</ul></li>);
  let t43;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <Github className="h-5 w-5" />;
    $[52] = t43;
  } else {
    t43 = $[52];
  }
  let t44;
  if ($[53] !== config.githubUrl) {
    t44 = <a href={config.githubUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent-foreground p-2 rounded-md transition-colors" aria-label="GitHub">{t43}</a>;
    $[53] = config.githubUrl;
    $[54] = t44;
  } else {
    t44 = $[54];
  }
  let t45;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" /></svg>;
    $[55] = t45;
  } else {
    t45 = $[55];
  }
  let t46;
  if ($[56] !== config.discordUrl) {
    t46 = <a href={config.discordUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent-foreground p-2 rounded-md transition-colors" aria-label="Discord">{t45}</a>;
    $[56] = config.discordUrl;
    $[57] = t46;
  } else {
    t46 = $[57];
  }
  let t47;
  if ($[58] !== t44 || $[59] !== t46) {
    t47 = <li className="flex items-center gap-4 pt-3 mt-2 border-t border-border">{t44}{t46}</li>;
    $[58] = t44;
    $[59] = t46;
    $[60] = t47;
  } else {
    t47 = $[60];
  }
  let t48;
  if ($[61] !== t34 || $[62] !== t37 || $[63] !== t38 || $[64] !== t40 || $[65] !== t41 || $[66] !== t42 || $[67] !== t47) {
    t48 = <ul className={t33}>{t34}{t37}{t38}{t40}{t41}{t42}{t47}</ul>;
    $[61] = t34;
    $[62] = t37;
    $[63] = t38;
    $[64] = t40;
    $[65] = t41;
    $[66] = t42;
    $[67] = t47;
    $[68] = t48;
  } else {
    t48 = $[68];
  }
  let t49;
  if ($[69] !== t32 || $[70] !== t48) {
    t49 = <div className={t31}>{t32}{t48}</div>;
    $[69] = t32;
    $[70] = t48;
    $[71] = t49;
  } else {
    t49 = $[71];
  }
  let t50;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" /></svg>;
    $[72] = t50;
  } else {
    t50 = $[72];
  }
  let t51;
  if ($[73] !== config.discordUrl) {
    t51 = <a href={config.discordUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground p-1.5 transition-colors" aria-label="Discord">{t50}</a>;
    $[73] = config.discordUrl;
    $[74] = t51;
  } else {
    t51 = $[74];
  }
  let t52;
  if ($[75] !== children || $[76] !== t51) {
    t52 = <div className="hidden min-[1280px]:flex items-center gap-1">{children}{t51}</div>;
    $[75] = children;
    $[76] = t51;
    $[77] = t52;
  } else {
    t52 = $[77];
  }
  let t53;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <ThemeToggle />;
    $[78] = t53;
  } else {
    t53 = $[78];
  }
  let t54;
  if ($[79] !== isAuthenticated) {
    t54 = isAuthenticated ? <Button asChild={true} className="bg-zinc-900 dark:bg-white text-white dark:text-black hover:bg-zinc-700 dark:hover:bg-zinc-200 font-medium w-full md:w-fit"><Link href="/dashboard" prefetch={true}>Dashboard</Link></Button> : <><Link href="/login" prefetch={true} className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden nav:block whitespace-nowrap">Log In</Link><Button asChild={true} className="bg-zinc-900 dark:bg-white text-white dark:text-black hover:bg-zinc-700 dark:hover:bg-zinc-200 font-medium w-full md:w-fit"><AuthLink href="/signup">Get Started</AuthLink></Button></>;
    $[79] = isAuthenticated;
    $[80] = t54;
  } else {
    t54 = $[80];
  }
  let t55;
  if ($[81] !== t52 || $[82] !== t54) {
    t55 = <div className="flex w-full flex-col space-y-3 sm:flex-row sm:gap-3 sm:space-y-0 md:w-fit items-center">{t52}{t53}{t54}</div>;
    $[81] = t52;
    $[82] = t54;
    $[83] = t55;
  } else {
    t55 = $[83];
  }
  let t56;
  if ($[84] !== t49 || $[85] !== t55) {
    t56 = <div className={t30}>{t49}{t55}</div>;
    $[84] = t49;
    $[85] = t55;
    $[86] = t56;
  } else {
    t56 = $[86];
  }
  return <header>{t5}<nav data-state={menuState && "active"} className={t7}>{t8}<div className={t10}><div className="relative flex flex-wrap items-center justify-between gap-6 py-3 nav:flex-nowrap nav:gap-0 nav:py-4">{t16}<div className="m-auto hidden items-center gap-1 nav:flex min-w-0">{t17}<NavigationMenu viewport={false} delayDuration={300}><NavigationMenuList className="flex gap-0.5 text-sm">{t18}{t21}{t22}<NavigationMenuItem>{t23}<NavigationMenuContent className="md:left-1/2 md:-translate-x-1/2"><ul className="grid grid-cols-2 gap-2 p-4 md:w-[520px] lg:w-[580px]">{productsLinks.map(_temp4)}</ul></NavigationMenuContent></NavigationMenuItem><NavigationMenuItem>{t24}<NavigationMenuContent className="md:left-1/2 md:-translate-x-1/2"><ul className="grid grid-cols-2 gap-2 p-4 md:w-[680px] lg:w-[820px] lg:grid-cols-3">{resourcesLinks.map(_temp5)}</ul></NavigationMenuContent></NavigationMenuItem><NavigationMenuItem>{t25}<NavigationMenuContent className="md:left-1/2 md:-translate-x-1/2"><ul className="grid grid-cols-2 gap-2 p-4 md:w-[520px] lg:w-[580px]">{aiLinks.map(_temp6)}</ul></NavigationMenuContent></NavigationMenuItem>{t28}{t29}</NavigationMenuList></NavigationMenu></div>{t56}</div></div></nav></header>;
};
function _temp(i) {
  return {
    name: i.title,
    href: i.href,
    external: i.external
  };
}
function _temp2(i_0) {
  return {
    name: i_0.title,
    href: i_0.href,
    external: i_0.external
  };
}
function _temp3(i_1) {
  return {
    name: i_1.title,
    href: i_1.href,
    external: i_1.external
  };
}
function _temp4(product) {
  return <IconMenuItem key={product.title} title={product.title} href={product.href} description={product.description} icon={product.icon} gradient={product.gradient} external={product.external} />;
}
function _temp5(link_0) {
  return <IconMenuItem key={link_0.title} title={link_0.title} href={link_0.href} description={link_0.description} icon={link_0.icon} gradient={link_0.gradient} external={link_0.external} />;
}
function _temp6(item) {
  return <IconMenuItem key={item.title} title={item.title} href={item.href} description={item.description} icon={item.icon} gradient={item.gradient} external={item.external} />;
}
function _temp7(item_0) {
  return <li key={item_0.name}>{item_0.external ? <a href={item_0.href} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent-foreground block py-2 duration-150 text-sm">{item_0.name}</a> : <Link href={item_0.href as Route} className="text-muted-foreground hover:text-accent-foreground block py-2 duration-150 text-sm" prefetch={true}>{item_0.name}</Link>}</li>;
}